import dedent from 'dedent';
import invariant from 'invariant';
import lodash from 'lodash';

import defined from '~app/core/defined';
import KeyPath from '~app/core/keyPath';
import setFunctionName from '~app/core/setFunctionName';

const MODEL_SENTINEL = Symbol('@@MODEL@@');

function maybeFreeze(obj) {
  if (typeof obj === 'object' && defined(obj)) {
    obj = Object.freeze(obj);
  }
  return obj;
}

const CommonMeta = {
  serializeField(fieldName, obj, ctx) {
    const fieldType = this.fields[fieldName];
    const serializedName = fieldType.serializedName || fieldName;
    const value = fieldType.serialize(
      obj[fieldName],
      createSerializationContext({ obj, modelType: this }, ctx),
    );
    return [serializedName, value];
  },

  deserializeField(fieldName, obj, ctx) {
    const fieldType = this.fields[fieldName];
    const serializedName = fieldType.serializedName || fieldName;
    const value = obj[serializedName];
    return fieldType.deserialize(
      value,
      createSerializationContext({ obj, modelType: this }, ctx),
    );
  },

  fieldPathFromSerializedKeyPath(path) {
    const srcParts = path.split('.');
    const targetParts = [];
    let targetModel = this;
    while (srcParts.length > 0) {
      const key = srcParts.shift();
      const fieldName = targetModel.serializedFieldNames[key];
      invariant(
        fieldName,
        dedent`
        could not resolve serialized field=${key} from \
        model=${targetModel.modelName} \
      `,
      );
      targetParts.push(fieldName);

      // find the next model in the chain
      if (srcParts.length > 0) {
        let fieldType = targetModel.fields[fieldName];
        while (!fieldType.modelType) {
          if (!fieldType.itemType) {
            invariant(
              false,
              dedent`
              cannot traverse into primitive types from model=${
                this.modelName
              } \
              path=${targetParts.join('.')} \
            `,
            );
          }
          fieldType = fieldType.itemType;
        }
        targetModel = fieldType.modelType;
      }
    }
    return targetParts.join('.');
  },
};

const CommonMethods = {
  equals(obj) {
    const modelType = getModelType(this);
    if (!(obj instanceof modelType)) return false;
    return lodash.isEqual(this, obj);
  },

  clone(values) {
    const modelType = getModelType(this);
    return modelType(values, this);
  },

  toObject() {
    const { fieldNames } = getModelType(this);
    const result = fieldNames
      .map(fieldName => {
        return [fieldName, this[fieldName]];
      })
      .reduce((obj, [k, v]) => {
        obj[k] = v;
        return obj;
      }, {});
    return Object.freeze(result);
  },
};

function createModelType({
  fields,
  preInit = null,
  init = null,
  name = 'NoNameModel',
  meta = {},
  properties = {},
  excludeFieldsFromSerialize = [],
  ...methods
}) {
  const fieldNames = Object.keys(fields);
  const recordSchema = {};
  const refs = {};
  const persistentFieldNames = fieldNames.filter(n => !fields[n].transient);
  const serializedFieldNames = Object.entries(fields)
    .filter(
      ([fieldName, fieldType]) =>
        !fieldType.transient && !excludeFieldsFromSerialize.includes(fieldName),
    )
    .map(([fieldName, fieldType]) => {
      const serializedName = fieldType.serializedName || fieldName;
      return [serializedName, fieldName];
    })
    .reduce((obj, [serializedName, fieldName]) => {
      obj[serializedName] = fieldName;
      return obj;
    }, {});

  fieldNames.forEach(name => {
    const field = fields[name];
    if (field.ref) {
      refs[name] = field.refKeyPath;
    } else {
      if (typeof field.default === 'function') {
        recordSchema[name] = undefined;
      } else {
        recordSchema[name] = field.default;
      }
    }
  });

  if (defined(meta.polymorphicOn)) {
    invariant(
      meta.polymorphicMap,
      dedent`
        must define a "polymorphicMap" attribute to use "polymorphicOn" \
        with model=${name} \
      `,
    );
  }

  function ModelRecord(values = {}, sourceModel = undefined) {
    if (!(this instanceof ModelRecord)) {
      return new ModelRecord(values, sourceModel);
    }
    if (defined(preInit)) {
      values = preInit(values);
    }
    invariant(
      !sourceModel || sourceModel instanceof ModelRecord,
      'cannot clone from a source object of another type',
    );
    Object.keys(values).forEach(k => {
      const fieldType = fields[k];
      invariant(
        fieldType && !fieldType.readonly,
        // use interpolation to avoid creating the message unless check fails
        'model=%s created with invalid key=%s',
        ModelRecord.modelName,
        k,
      );
    });
    values = Object.entries(fields)
      .filter(([name, fieldType]) => !fieldType.readonly)
      .map(([name, fieldType]) => {
        let v = values[name];
        if (v === undefined) {
          if (sourceModel) {
            v = sourceModel[name];
          } else if (typeof fieldType.default === 'function') {
            v = fieldType.default();
          } else if (
            Object.prototype.hasOwnProperty.call(fieldType, 'default')
          ) {
            v = fieldType.default;
          }
          // if the user didn't explicitly define "default" then set it null
          else {
            v = null;
          }
        }
        return [name, maybeFreeze(v)];
      })
      .reduce((obj, [k, v]) => {
        obj[k] = v;
        return obj;
      }, {});
    if (defined(init)) {
      values = init(values);
    }
    Object.assign(this, values);
    Object.freeze(this);
  }

  ModelRecord.modelName = name;
  setFunctionName(ModelRecord, name);

  ModelRecord[MODEL_SENTINEL] = true;

  Object.assign(ModelRecord, {
    fields,
    fieldNames,
    refs,
    persistentFieldNames,
    serializedFieldNames,
    excludeFieldsFromSerialize,
    ...CommonMeta,
    ...meta,
  });

  Object.entries({ ...CommonMethods, ...methods }).forEach(([key, fn]) => {
    Object.defineProperty(ModelRecord.prototype, key, {
      value: fn,
      enumerable: false,
      writable: true,
      configurable: true,
    });
  });

  // define readonly getters for refs
  Object.entries(refs).forEach(([key, path]) => {
    Object.defineProperty(ModelRecord.prototype, key, {
      get: function () {
        return KeyPath.get(this, path);
      },
    });
  });
  Object.entries(properties).forEach(([name, get]) => {
    Object.defineProperty(ModelRecord.prototype, name, { get });
  });
  return ModelRecord;
}

function getModelType(obj) {
  if (obj) {
    if (obj[MODEL_SENTINEL]) {
      return obj;
    } else if (obj.constructor && obj.constructor[MODEL_SENTINEL]) {
      return obj.constructor;
    }
  }
  throw new Error('object is not a model type');
}

function isModelType(maybeModel) {
  return !!(maybeModel && maybeModel[MODEL_SENTINEL]);
}

function isModelInstance(maybeModel) {
  return !!(
    maybeModel &&
    maybeModel.constructor &&
    maybeModel.constructor[MODEL_SENTINEL]
  );
}

function SerializationContext(opts, baseContext) {
  if (opts instanceof SerializationContext && baseContext === undefined) {
    return opts;
  }
  Object.assign(this, {
    ...baseContext,
    ...opts,
  });
  Object.freeze(this);
}

Object.assign(SerializationContext.prototype, {
  clone(opts) {
    return new SerializationContext(opts, this);
  },
});

function createSerializationContext(...args) {
  return new SerializationContext(...args);
}

function serializeModel(model, ctx) {
  if (!defined(model)) return null;
  const modelType = Model.getType(model);
  ctx = createSerializationContext({ obj: model, modelType }, ctx);
  return Object.entries(modelType.fields)
    .filter(
      ([fieldName, fieldType]) =>
        !fieldType.transient &&
        !modelType.excludeFieldsFromSerialize.includes(fieldName),
    )
    .map(([fieldName, fieldType]) => {
      const fieldValue = model[fieldName];
      const serializedName = fieldType.serializedName || fieldName;
      return [serializedName, fieldType.serialize(fieldValue, ctx)];
    })
    .reduce((obj, [fieldName, fieldValue]) => {
      obj[fieldName] = fieldValue;
      return obj;
    }, {});
}

function simpleObjectToModel(ctx) {
  const { obj, modelType, unknownFields = 'ignore' } = ctx;
  const unknownKeys = new Set(Object.keys(obj));
  const modelValues = Object.entries(modelType.fields)
    .filter(
      ([fieldName, fieldType]) => !fieldType.readonly && !fieldType.transient,
    )
    .map(([fieldName, fieldType]) => {
      const serializedName = fieldType.serializedName || fieldName;
      unknownKeys.delete(serializedName);
      let value = obj[serializedName];
      if (value !== undefined) {
        value = fieldType.deserialize(value, ctx);
      }
      return [fieldName, value];
    })
    .reduce((obj, [fieldName, fieldValue]) => {
      obj[fieldName] = fieldValue;
      return obj;
    }, {});
  if (unknownFields === 'raise' && unknownKeys.size > 0) {
    throw new Error(dedent`
      unknown keys=${Array.from(unknownKeys).sort()} found while \
      deserializing model=${modelType.modelName} \
    `);
  }
  return modelType(modelValues);
}

function polymorphicObjectToModel(ctx) {
  // determine the subtype based on the value of the polymorphicOn field
  const { obj, modelType } = ctx;
  const fieldName = modelType.polymorphicOn;
  const fieldType = modelType.fields[fieldName];
  const serializedName = fieldType.serializedName || fieldName;
  const cstruct = obj[serializedName];
  const value = fieldType.deserialize(cstruct, ctx);
  const subType = modelType.polymorphicMap[value];

  // unable to find a polymorphic mapping, just return the parent type
  if (!defined(subType)) {
    return simpleObjectToModel(ctx);
  }

  return simpleObjectToModel(ctx.clone({ modelType: subType }));
}

function deserializeModel(obj, modelType, opts) {
  if (!isModelType(modelType)) {
    opts = modelType;
    invariant(opts.modelType, `options must contain a modelType property`);
    modelType = opts.modelType;
  }
  if (obj instanceof modelType) return obj;
  const ctx = createSerializationContext({ obj, modelType }, opts);
  if (!defined(obj)) return null;
  if (modelType.polymorphicOn) {
    return polymorphicObjectToModel(ctx);
  }
  return simpleObjectToModel(ctx);
}

function AdapterStack(rootToModelType, rootFromModelType, sourceMapping) {
  Object.assign(this, {
    rootToModelType,
    rootFromModelType,
    sourceMapping,
    parent: null,
    toFieldName: null,
    toFieldType: { modelType: rootToModelType },
    listCount: 0,
  });
}

Object.assign(AdapterStack.prototype, {
  extend(opts) {
    const result = Object.create(this);
    return Object.assign(result, {
      parent: this,
      ...opts,
    });
  },

  withModelField(toFieldName) {
    return this.extend({
      toFieldName,
      toFieldType: this.toFieldType.modelType.fields[toFieldName],
    });
  },

  withItems() {
    return this.extend({
      listCount: this.listCount + 1,
      toFieldName: null,
      toFieldType: this.toFieldType.itemType,
    });
  },

  getPathSegments() {
    const parts = [];
    let obj = this;
    while (obj !== null) {
      if (obj.toFieldName) {
        parts.push(obj.toFieldName);
      }
      obj = obj.parent;
    }
    parts.reverse();
    return parts;
  },

  getPath() {
    return this.getPathSegments().join('.');
  },
});

function AdapterContext() {
  this.indices = [];
}

function _getFromPath(toPath, sourceMapping) {
  const toPathSegments = toPath.split('.');
  // we have a key path in the "to" model tree that we need to map to a
  // path in the "from" model tree which we will do using the user-provided
  // mapping as well as attempting to infer paths for anything matching
  // by name

  // split the toPath into a "basePath" and an "inferredPathParts"
  const inferredPathParts = [];
  let basePathParts = [];
  while (toPathSegments.length > 0) {
    const toPath = toPathSegments.join('.');
    const basePath = sourceMapping[toPath];

    // do not worry about "null" here because we already filtered out
    // noop paths at a higher level
    if (basePath === undefined) {
      inferredPathParts.push(toPathSegments.pop());
    } else {
      basePathParts = basePath.split('.');
      break;
    }
  }
  inferredPathParts.reverse();
  const fromPathParts = basePathParts.concat(inferredPathParts);
  return fromPathParts.join('.');
}

function _resolvePathSegments(segments, obj, indices, listIndex = 0) {
  return segments.reduce((previous, current) => {
    while (lodash.isArray(previous) && listIndex < indices.length) {
      const itemIndex = indices[listIndex++];
      previous = previous[itemIndex];
    }
    if (previous === undefined) return undefined;
    if (current === '') return previous;
    return previous[current];
  }, obj);
}

// use pathSegments and maxListIndex to traverse the model tree to create
// a getter function that accepts indices for each list and returns the
// item at that location in the model object
function _createGetterFromModel(rootModelType, path, maxListCount) {
  const pathSegments = path.split('.');
  if (!rootModelType) {
    return (obj, indices) => _resolvePathSegments(pathSegments, obj, indices);
  }
  const initialFieldName = pathSegments.shift();
  let fieldType = rootModelType.fields[initialFieldName];
  let curPath = [initialFieldName];
  const subPaths = [];
  let opaquePaths = [];
  while (pathSegments.length > 0 || subPaths.length < maxListCount) {
    const { modelType, itemType } = fieldType;
    if (modelType) {
      const fieldName = pathSegments.shift();
      curPath.push(fieldName);
      fieldType = modelType.fields[fieldName];
    } else if (itemType) {
      subPaths.push(curPath);
      curPath = [];
      fieldType = itemType;
    } else {
      // we got here for one of two reasons:
      // 1. the path extends past the model into opaque objects.
      //    For example, fromModel is {a: ...} and path is "a.b" then we
      //    expect that curPath = ["a"] and pathSegments = ["b"] at this
      //    point and we'll register the remaining segments as an opaque path
      // 2. the path shorter than the model and we actually don't need to
      //    use all the indices so we'll stop here. For example, fromModel is
      //    {a: 'foo', b: [...]} and toModel is {c: [...]} and the mapping
      //    is {"c": "b", "c.a": "a"} to copy the top-level "a" into every
      //    object in "c"
      opaquePaths = pathSegments;
      break;
    }
  }
  invariant(
    subPaths.length <= maxListCount,
    dedent`
      expected at most ${maxListCount} lists but found ${subPaths.length} \
      in path=${path} for model=${rootModelType.modelName} \
    `,
  );
  subPaths.push(curPath);
  return function (obj, indices) {
    invariant(
      indices.length === maxListCount,
      dedent`
        expected %s list indices to resolve path but received \
        %s for model=%s \
      `,
      maxListCount,
      indices.length,
      rootModelType.modelName,
    );
    if (!defined(obj)) return obj;
    let listIndex = 0;
    for (const subpath of subPaths) {
      for (const fieldName of subpath) {
        obj = obj[fieldName];
        if (!defined(obj)) return obj;
      }
      if (listIndex < subPaths.length - 1) {
        const itemIndex = indices[listIndex++];
        obj = obj[itemIndex];
        if (!defined(obj)) return obj;
      }
    }
    if (opaquePaths.length > 0) {
      obj = _resolvePathSegments(opaquePaths, obj, indices, listIndex);
    }
    return obj;
  };
}

function _validatePath(rootModelType, path) {
  const pathSegments = path.split('.');
  let fieldType = { modelType: rootModelType };
  while (pathSegments.length > 0) {
    const { modelType, itemType } = fieldType;
    if (modelType) {
      const fieldName = pathSegments.shift();
      fieldType = modelType.fields[fieldName];
      invariant(
        defined(fieldType),
        dedent`
        invalid path=${path} for model=${rootModelType.modelName}, \
        could not find a matching subpath=${fieldName} \
      `,
      );
    } else if (itemType) {
      fieldType = itemType;
    } else {
      break;
    }
  }
}

function _createFromPathGetter(stack) {
  const fromPath = _getFromPath(stack.getPath(), stack.sourceMapping);
  _validatePath(stack.rootFromModelType, fromPath);
  return _createGetterFromModel(
    stack.rootFromModelType,
    fromPath,
    stack.listCount,
  );
}

const _noop = () => undefined;

function _createPrimitiveAdapter(stack) {
  // shortcut if the user asked us not to adapt this part of the model
  if (stack.sourceMapping[stack.getPath()] === null) {
    return _noop;
  }

  const getAttr = _createFromPathGetter(stack);
  return function (ctx, obj) {
    return getAttr(obj, ctx.indices);
  };
}

function _createListAdapter(stack) {
  // shortcut if the user asked us not to adapt this part of the model
  if (stack.sourceMapping[stack.getPath()] === null) {
    return _noop;
  }

  const getItems = _createFromPathGetter(stack);

  const itemStack = stack.withItems();
  const adapter =
    // listof(model(...))
    itemStack.toFieldType.modelType
      ? _createModelAdapter(itemStack)
      : // listof(listof(...))
      itemStack.toFieldType.itemType
      ? _createListAdapter(itemStack)
      : // listof(...)
        _createPrimitiveAdapter(itemStack);
  return function (ctx, obj) {
    const results = [];
    const items = getItems(obj, ctx.indices);
    if (!defined(items)) {
      return items;
    }
    const indexKey = ctx.indices.length;
    ctx.indices.push(0);
    // eslint-disable-next-line no-unused-vars
    for (const _ of items) {
      results.push(adapter(ctx, obj));
      ctx.indices[indexKey] += 1;
    }
    ctx.indices.pop();
    return results;
  };
}

function _createModelAdapter(stack) {
  // shortcut if the user asked us not to adapt this part of the model
  if (stack.sourceMapping[stack.getPath()] === null) {
    return _noop;
  }

  const getBaseObject =
    stack.parent !== null ? _createFromPathGetter(stack) : obj => obj;

  const toModelType = stack.toFieldType.modelType;
  const fieldAdapters = {};
  for (const toFieldName of toModelType.fieldNames) {
    const toFieldStack = stack.withModelField(toFieldName);
    const { toFieldType } = toFieldStack;
    if (toFieldType.readonly) continue;
    if (toFieldType.modelType) {
      fieldAdapters[toFieldName] = _createModelAdapter(toFieldStack);
    } else if (toFieldType.itemType) {
      fieldAdapters[toFieldName] = _createListAdapter(toFieldStack);
    } else {
      fieldAdapters[toFieldName] = _createPrimitiveAdapter(toFieldStack);
    }
  }
  return function (ctx, obj) {
    const base = getBaseObject(obj, ctx.indices);
    if (!defined(base)) {
      return base;
    }
    const values = {};
    for (const [fieldName, fieldAdapter] of Object.entries(fieldAdapters)) {
      values[fieldName] = fieldAdapter(ctx, obj);
    }
    return toModelType(values);
  };
}

function createModelAdapter(toModelType, fromModelType, options) {
  if (fromModelType !== null && !isModelType(fromModelType)) {
    invariant(
      options === undefined,
      dedent`
      expected a model type as the second argument \
    `,
    );
    options = fromModelType || {};
    fromModelType = null;
  }
  options = options || {};
  const stack = new AdapterStack(
    toModelType,
    fromModelType,
    options.mapping || {},
  );
  const adapter = _createModelAdapter(stack);
  return function (obj) {
    if (fromModelType) {
      invariant(
        obj instanceof fromModelType,
        `cannot adapt object, is not of type=${fromModelType.modelName}`,
      );
    }
    const ctx = new AdapterContext();
    return adapter(ctx, obj);
  };
}

function adaptModel(obj, toModelType, options) {
  const fromModelType = isModelInstance(obj) ? getModelType(obj) : null;
  const adapter = createModelAdapter(toModelType, fromModelType, options);
  return adapter(obj);
}

const Model = {
  createType: createModelType,
  getType: getModelType,
  is: isModelType,
  isInstance: isModelInstance,
  serialize: serializeModel,
  deserialize: deserializeModel,
  createAdapter: createModelAdapter,
  createSerializationContext: createSerializationContext,
  adapt: adaptModel,
};

export default Model;
